import { useState, useEffect, useCallback } from 'react';
import { messageService } from '../services/messageService';

export const useMessages = (params = {}) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0
  });

  const fetchMessages = useCallback(async (fetchParams = {}) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await messageService.getMessages({
        ...params,
        ...fetchParams
      });
      
      setMessages(response.data.data || []);
      
      if (response.data.pagination) {
        setPagination(response.data.pagination);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch messages');
      console.error('Error fetching messages:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMessages(params);
  }, [fetchMessages]);

  const sendMessage = async (data) => {
    try {
      const response = await messageService.sendMessage(data);
      await fetchMessages(); // Refresh the list
      return response.data;
    } catch (err) {
      throw err.response?.data?.message || 'Failed to send message';
    }
  };

  const replyToMessage = async (messageId, data) => {
    try {
      const response = await messageService.replyToMessage(messageId, data);
      await fetchMessages(); // Refresh the list
      return response.data;
    } catch (err) {
      throw err.response?.data?.message || 'Failed to reply to message';
    }
  };

  const updateMessage = async (id, data) => {
    try {
      const response = await messageService.updateMessage(id, data);
      await fetchMessages(); // Refresh the list
      return response.data;
    } catch (err) {
      throw err.response?.data?.message || 'Failed to update message';
    }
  };

  const deleteMessage = async (id) => {
    try {
      await messageService.deleteMessage(id);
      await fetchMessages(); // Refresh the list
    } catch (err) {
      throw err.response?.data?.message || 'Failed to delete message';
    }
  };

  const markAsRead = async (id) => {
    try {
      await messageService.markAsRead(id);
      await fetchMessages(); // Refresh the list
    } catch (err) {
      throw err.response?.data?.message || 'Failed to mark message as read';
    }
  };

  const markAsUnread = async (id) => {
    try {
      await messageService.markAsUnread(id);
      await fetchMessages(); // Refresh the list
    } catch (err) {
      throw err.response?.data?.message || 'Failed to mark message as unread';
    }
  };

  const refreshMessages = () => {
    fetchMessages();
  };

  const loadMore = () => {
    if (pagination.page < pagination.totalPages) {
      fetchMessages({ page: pagination.page + 1 });
    }
  };

  return {
    messages,
    loading,
    error,
    pagination,
    sendMessage,
    replyToMessage,
    updateMessage,
    deleteMessage,
    markAsRead,
    markAsUnread,
    refreshMessages,
    loadMore,
    fetchMessages
  };
};

export const useInbox = (params = {}) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchInbox = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await messageService.getInbox(params);
      setMessages(response.data.data || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch inbox');
      console.error('Error fetching inbox:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchInbox(params);
  }, [fetchInbox]);

  return {
    messages,
    loading,
    error,
    refreshInbox: fetchInbox
  };
};

export const useSentMessages = (params = {}) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchSentMessages = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await messageService.getSent(params);
      setMessages(response.data.data || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch sent messages');
      console.error('Error fetching sent messages:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSentMessages(params);
  }, [fetchSentMessages]);

  return {
    messages,
    loading,
    error,
    refreshSentMessages: fetchSentMessages
  };
};

export const useMessage = (id) => {
  const [message, setMessage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchMessage = useCallback(async () => {
    if (!id) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const response = await messageService.getMessage(id);
      setMessage(response.data.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch message');
      console.error('Error fetching message:', err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchMessage();
  }, [fetchMessage]);

  return {
    message,
    loading,
    error,
    refreshMessage: fetchMessage
  };
};

export const useMessageThread = (messageId) => {
  const [thread, setThread] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchMessageThread = useCallback(async () => {
    if (!messageId) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const response = await messageService.getMessageThread(messageId);
      setThread(response.data.data || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch message thread');
      console.error('Error fetching message thread:', err);
    } finally {
      setLoading(false);
    }
  }, [messageId]);

  useEffect(() => {
    fetchMessageThread();
  }, [fetchMessageThread]);

  return {
    thread,
    loading,
    error,
    refreshMessageThread: fetchMessageThread
  };
};

export const useUnreadCount = () => {
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchUnreadCount = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await messageService.getUnreadCount();
      setUnreadCount(response.data.data?.count || 0);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch unread count');
      console.error('Error fetching unread count:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUnreadCount();
    
    // Refresh unread count every 30 seconds
    const interval = setInterval(fetchUnreadCount, 30000);
    
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  return {
    unreadCount,
    loading,
    error,
    refreshUnreadCount: fetchUnreadCount
  };
};
